import {ActionIcon, Badge, Menu} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {IconDotsVertical, IconTrash} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import {CourseDefinitionContentTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ContentModal} from 'src/design/molecules/ContentModal';
import {useGetNotificationRuleListQuery} from 'src/redux/queries/notificationRule';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {QuizQuestionWithKey} from 'src/app/training/courseDefaults';
import EditContentDefinitionDemonstration from 'src/pages/Modules/Training/shared/editCourseDefinition/EditContentDefinitionDemonstration';
import EditContentDefinitionLesson from 'src/pages/Modules/Training/shared/editCourseDefinition/EditContentDefinitionLesson';
import EditContentDefinitionQuiz from 'src/pages/Modules/Training/shared/editCourseDefinition/EditContentDefinitionQuiz';
import {contentTypeLabel, contentTypes} from './contentTypes';
import {InlineVideoUpload} from './InlineVideoUpload';
import {QuizQuestionsCanvas} from './QuizQuestionsCanvas';

export interface ContentEditorPanelProps {
  content: CourseDefinitionContentResponse;
  disabled?: boolean;
  onContentChange: (content: CourseDefinitionContentResponse) => void;
  onDelete: () => void;
}

/**
 * Build tab's Column 2 editor for a single selected content item — the only
 * editor mounted at a time (the Build tab unmounts every sibling), which is
 * what kills the mount-everything perf defect the previous tabbed
 * `EditCourseDefinitionContents` had (every content's editor, including its
 * data queries, mounted simultaneously).
 *
 * Content type is a badge plus an overflow-menu action rather than a radio row
 * at the top of the editor: changing it swaps the entire field set, so it is a
 * structural decision, not a routine field. For quizzes the questions render
 * inline below the quiz fields via `QuizQuestionsCanvas`.
 */
export const ContentEditorPanel: React.FC<ContentEditorPanelProps> = ({
  content,
  disabled,
  onContentChange,
  onDelete,
}) => {
  const {
    data: notificationRulePayload,
    isFetching,
    isLoading,
  } = useGetNotificationRuleListQuery({
    start: 0,
    length: 10000,
    courseContentInvariantId: content.invariantId,
  });
  const notificationRulesLoading = isFetching || isLoading;
  const notificationRules = notificationRulePayload?.data ?? [];
  const [showLinkedModulesModal, setShowLinkedModulesModal] = useState(false);

  const handleContentTypeChange = (type: CourseDefinitionContentTypeEnum) => {
    onContentChange({...content, type});
  };

  const typeLocked =
    notificationRulesLoading === false && notificationRules.length > 0;

  const questions = (content.quiz?.questions ?? []) as QuizQuestionWithKey[];

  const handleQuestionsChange = (updated: QuizQuestionWithKey[]) => {
    onContentChange({...content, quiz: {...content.quiz!, questions: updated}});
  };

  return (
    <Box>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Badge variant="light" size="lg" style={{textTransform: 'none'}}>
          {contentTypeLabel(content.type)}
        </Badge>
        <Menu shadow="md" width={220} position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Content actions"
              disabled={disabled}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Change type</Menu.Label>
            {contentTypes.map(type => (
              <Menu.Item
                key={type}
                disabled={typeLocked || type === content.type}
                onClick={() => handleContentTypeChange(type)}>
                {contentTypeLabel(type)}
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={onDelete}>
              Delete content
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {typeLocked && (
        <Alert
          color="yellow"
          mt="sm"
          mb="sm"
          title="Connected to other modules">
          This content is linked to {notificationRules.length} notification
          rule(s), so its type cannot be changed.{' '}
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setShowLinkedModulesModal(true)}>
            View rules
          </Button>
        </Alert>
      )}

      <ContentModal
        title="Connected Notification Rules"
        open={showLinkedModulesModal}
        onCloseCallback={() => setShowLinkedModulesModal(false)}>
        <ul>
          {notificationRules.map(rule => (
            <li key={rule.id}>
              {rule.name ?? rule.id}:{' '}
              <a
                href={PathUtility.NotificationRuleView(rule.id)}
                target="_blank"
                rel="noreferrer">
                Open
              </a>
            </li>
          ))}
        </ul>
      </ContentModal>

      <Divider my="sm" />

      <fieldset
        disabled={disabled}
        style={{border: 'none', padding: 0, margin: 0, minWidth: 0}}>
        {content.type === CourseDefinitionContentTypeEnum.Lesson && (
          <EditContentDefinitionLesson
            content={content}
            onContentChange={onContentChange}
            renderVideoUploadControl={onUploaded => (
              <InlineVideoUpload onUploaded={onUploaded} />
            )}
          />
        )}
        {content.type === CourseDefinitionContentTypeEnum.Quiz && (
          <>
            <EditContentDefinitionQuiz
              content={content}
              onContentChange={onContentChange}
            />
            <Divider my="md" />
            <QuizQuestionsCanvas
              questions={questions}
              disabled={disabled}
              onQuestionsChange={handleQuestionsChange}
            />
          </>
        )}
        {content.type === CourseDefinitionContentTypeEnum.Demonstration && (
          <EditContentDefinitionDemonstration
            content={content}
            onContentChange={onContentChange}
          />
        )}
      </fieldset>
    </Box>
  );
};
